import { useTenantModules } from '../../hooks/useTenantModules';
import type { ModuleKey } from '../../hooks/useTenantModules';
import { useUserRoles } from '../../hooks/useUserRoles';
import Loading from './Loading';
import ModuleNotBooked from './ModuleNotBooked';
import { useTranslation } from 'react-i18next';
import { isPlatformLevelRole } from '../../lib/user-roles';

interface ModuleGuardProps {
  moduleKey: ModuleKey | string;
  children: JSX.Element;
  /**
   * When true (default), platform staff (the superadmin tier and
   * platform-admin) bypass the module gate and always see the page even if
   * the current tenant has the module disabled. Explicitly pass ``false``
   * only if a route must hard-enforce the tenant module — which is almost
   * never the right choice.
   */
  allowPrivilegedBypass?: boolean;
}

/**
 * Route guard that shows "Modul nicht gebucht" at the same address when a
 * module is disabled for the current tenant (it used to redirect silently).
 *
 * Platform staff bypass the check by default — they configure all tenants
 * and need to reach every page regardless of tenant module state. A tenant's
 * own administrators (`admin`, `tenant_admin`) do not: the api-gateway answers
 * their calls to an unbooked module with 404, so the page could not work.
 */
export default function ModuleGuard({
  moduleKey,
  children,
  allowPrivilegedBypass = true,
}: ModuleGuardProps) {
  const { t } = useTranslation();
  const { isModuleEnabled, isLoading, isError, refetch, userRole } = useTenantModules();
  const { roles, isSuperuser, isLoading: rolesLoading } = useUserRoles();

  // Token roles first, the gateway-reported role as a fallback.
  const isPrivilegedUser =
    isSuperuser || roles.some(isPlatformLevelRole) ||
    isPlatformLevelRole(String(userRole || ''));

  if (isLoading || rolesLoading) {
    return <Loading />;
  }

  if (isError && !(allowPrivilegedBypass && isPrivilegedUser)) {
    return (
      <div role="alert" className="p-6 space-y-4">
        <p>{t('errors.modulesLoadFailed', 'Module access could not be loaded. Please try again.')}</p>
        <button type="button" onClick={refetch} className="px-4 py-2 rounded bg-primary-600 text-white">
          {t('common.retry', 'Try again')}
        </button>
      </div>
    );
  }

  if (!isModuleEnabled(moduleKey) && !(allowPrivilegedBypass && isPrivilegedUser)) {
    return <ModuleNotBooked />;
  }

  return children;
}
